import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  PaginationItem,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as XLSX from "xlsx";
import RevenueInsightsChart, { RevenueInsightCounts, RevenueInsightPoint } from "./RevenueInsightsChart";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TruncatedTitle from "./TruncatedTitle";
import themestyle from "../theme";
import { useQuery } from "@apollo/client/react";
import {
  GET_ATTENDANCE_REPORT,
  GET_CLASS_PERFORMANCE_REPORT,
  GET_INSTRUCTOR_ENGAGEMENT_REPORT,
  GET_SUPERADMIN_REVENUE_INSIGHTS,
  GET_STUDENT_OVERVIEW_REPORT,
} from "../graphql/query";
import { GET_CLASS_LIST, GET_INSTRUCTOR_LIST } from "../graphql/query";
import {
  AttendanceReport,
  GetAttendanceReportResponse,
  GetClassPerformanceReportResponse,
  GetInstructorEngagementReportResponse,
  GetStudentOverviewReportResponse,
} from "../interfaces/report.interface";
import AppDatePicker from "./AppDatePicker";

const statusChipStyles: Record<string, { bg: string; fg: string; border: string }> = {
  COMPLETED: { bg: "#E8F7EE", fg: "#0F8A57", border: "#CDEFD9" },
  IN_PROGRESS: { bg: "#EEF4FF", fg: "#3162D0", border: "#D6E4FF" },
  SCHEDULED: { bg: "#F4F5F7", fg: "#4B5563", border: "#E5E7EB" },
};

const performanceChipStyles: Record<string, { bg: string; fg: string }> = {
  HIGH_DEMAND: { bg: "#FDE8E5", fg: "#C2410C" },
  MEDIUM: { bg: "#DBEAFE", fg: "#1D4ED8" },
  LOW: { bg: "#F3F4F6", fg: "#6B7280" },
};

const formatDate = (dateStr: string) => {
  if (!dateStr || !dateStr.includes("-")) return dateStr;
  return dateStr.split("-").reverse().join("-");
};

const initials = (name: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "S";
};

const truncateText = (value: string, maxLength: number) => {
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isValidDateInputValue = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime()) && formatDateInputValue(parsed) === value;
};

const panelSx = {
  border: `1px solid ${themestyle.colors.bordercolor}`,
  borderRadius: "18px",
  boxShadow: "0px 16px 40px rgba(7, 24, 51, 0.06)",
};

const ReportTableState = ({
  colSpan,
  message,
  loading = false,
}: {
  colSpan: number;
  message: string;
  loading?: boolean;
}) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 10 }}>
      <Box
        sx={{
          width: "100%",
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        {loading ? (
          <CircularProgress
            size={30}
            thickness={3}
            sx={{ color: themestyle.colors.activeGreen }}
          />
        ) : null}
        <Typography
          variant="body2"
          sx={{ color: "#9CA3AF", fontWeight: 600, textAlign: "center" }}
        >
          {message}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

type ExportOption = "attendance" | "class_performance" | "instructor_engagement" | "student_overview" | "all";

const EXPORT_OPTIONS: Array<{ value: ExportOption; title: string }> = [
  { value: "all", title: "All Details" },
  { value: "attendance", title: "Attendance" },
  { value: "class_performance", title: "Class Performance" },
  { value: "instructor_engagement", title: "Instructor Engagement" },
  { value: "student_overview", title: "Student Overview" },
];

const INDIVIDUAL_EXPORT_OPTIONS: ExportOption[] = [
  "attendance",
  "class_performance",
  "instructor_engagement",
  "student_overview",
];

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const defaultTimeRange = "This Month";
  const defaultMonthYear = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const defaultSpecificDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultSpecificYear = useMemo(() => new Date().getFullYear().toString(), []);
  const [timeRange, setTimeRange] = useState(defaultTimeRange);
  const [monthYear, setMonthYear] = useState(defaultMonthYear); // YYYY-MM
  const [specificDate, setSpecificDate] = useState(defaultSpecificDate); // YYYY-MM-DD
  const [specificYear, setSpecificYear] = useState(defaultSpecificYear);
  const [classFilter, setClassFilter] = useState("All Classes");
  const [instructorFilter, setInstructorFilter] = useState("All Instructors");
  const [searchTerm, setSearchTerm] = useState("");
  const [attendancePage, setAttendancePage] = useState(1);
  const [studentPage, setStudentPage] = useState(1);
  const [performancePage, setPerformancePage] = useState(1);
  const [engagementPage, setEngagementPage] = useState(1);
  const [rangeStart, setRangeStart] = useState(() => {
    const now = new Date();
    return formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [rangeEnd, setRangeEnd] = useState(() => formatDateInputValue(new Date()));

  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExportOptions, setSelectedExportOptions] = useState<ExportOption[]>([]);
  const ATTENDANCE_PER_PAGE = 5;
  const STUDENT_PER_PAGE = 5;
  const PERFORMANCE_PER_PAGE = 5;
  const ENGAGEMENT_PER_PAGE = 5;
  const canResetReportFilters =
    searchTerm !== "" ||
    timeRange !== defaultTimeRange ||
    monthYear !== defaultMonthYear ||
    specificDate !== defaultSpecificDate ||
    specificYear !== defaultSpecificYear ||
    classFilter !== "All Classes" ||
    instructorFilter !== "All Instructors";

  const ResponsivePagination = ({ 
    page, 
    count, 
    onChange 
  }: { 
    page: number; 
    count: number; 
    onChange: (page: number) => void 
  }) => {
    if (isMobile) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            sx={{ 
              border: "1px solid #E5E7EB", 
              bgcolor: "#fff",
              "&:hover": { bgcolor: "#F9FAFB" },
              width: 32,
              height: 32
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= count}
            onClick={() => onChange(page + 1)}
            sx={{ 
              border: "1px solid #E5E7EB", 
              bgcolor: "#fff",
              "&:hover": { bgcolor: "#F9FAFB" },
              width: 32,
              height: 32
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }

    return (
      <Pagination
        count={count}
        page={page}
        onChange={(_, val) => onChange(val)}
        size="small"
        siblingCount={0}
        boundaryCount={1}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            sx={{
              mx: 0.25,
              fontWeight: 700,
              color: "#6B7280",
              "&.Mui-selected": {
                bgcolor: "#a3d65c",
                color: "#fff",
                "&:hover": { bgcolor: "#86b347" },
              },
              borderRadius: "50%",
              width: 32,
              height: 32,
            }}
          />
        )}
      />
    );
  };
  const dateVariables = useMemo(() => {
    const today = new Date();
    let startDate = "";
    let endDate = today.toISOString().split("T")[0];

    try {
      if (timeRange === "Today") {
        startDate = endDate;
      } else if (timeRange === "This Week") {
        const d = new Date(today);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start or keep Sunday
        startDate = new Date(d.setDate(diff)).toISOString().split("T")[0];
      } else if (timeRange === "This Month") {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
      } else if (timeRange === "This Year") {
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0];
      } else if (timeRange === "Specific Month" && monthYear && monthYear.includes("-")) {
        const [year, month] = monthYear.split("-").map(Number);
        if (!isNaN(year) && !isNaN(month)) {
          startDate = new Date(year, month - 1, 1).toISOString().split("T")[0];
          const lastDay = new Date(year, month, 0).toISOString().split("T")[0];
          endDate = lastDay;
        }
      } else if (timeRange === "Specific Date" && specificDate) {
        startDate = specificDate;
        endDate = specificDate;
      } else if (timeRange === "Specific Year" && specificYear) {
        const year = Number(specificYear);
        if (!isNaN(year)) {
          startDate = new Date(year, 0, 1).toISOString().split("T")[0];
          endDate = new Date(year, 11, 31).toISOString().split("T")[0];
        }
      }
    } catch (e) {
      console.error("Date calculation error:", e);
      startDate = endDate;
    }
    return { startDate, endDate };
  }, [timeRange, monthYear, specificDate, specificYear]);

  const { data: classListData } = useQuery(GET_CLASS_LIST, { variables: { page: 1, perPage: 100 } });
  const { data: instructorListData } = useQuery(GET_INSTRUCTOR_LIST, { variables: { page: 1, perPage: 100 } });
  const { data: attendanceData, loading: attendanceLoading } = useQuery<GetAttendanceReportResponse>(GET_ATTENDANCE_REPORT, {
    variables: {
      ...dateVariables,
      classId: classFilter === "All Classes" ? undefined : classFilter,
      instructorId: instructorFilter === "All Instructors" ? undefined : instructorFilter,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: performanceData, loading: performanceLoading } = useQuery<GetClassPerformanceReportResponse>(GET_CLASS_PERFORMANCE_REPORT, { fetchPolicy: "cache-and-network" });
  const { data: engagementData, loading: engagementLoading } = useQuery<GetInstructorEngagementReportResponse>(
    GET_INSTRUCTOR_ENGAGEMENT_REPORT,
    { fetchPolicy: "cache-and-network" }
  );
  const { data: studentOverviewData, loading: overviewLoading } = useQuery<GetStudentOverviewReportResponse>(GET_STUDENT_OVERVIEW_REPORT, { fetchPolicy: "cache-and-network" });
  const hasRangeStart = rangeStart.trim().length > 0;
  const hasRangeEnd = rangeEnd.trim().length > 0;
  const canResetRevenueDateRange = hasRangeStart || hasRangeEnd;
  const isRangeStartValid = !hasRangeStart || isValidDateInputValue(rangeStart);
  const isRangeEndValid = !hasRangeEnd || isValidDateInputValue(rangeEnd);
  const isRevenueRangeValid =
    hasRangeStart &&
    hasRangeEnd &&
    isRangeStartValid &&
    isRangeEndValid &&
    new Date(`${rangeStart}T00:00:00`).getTime() <= new Date(`${rangeEnd}T00:00:00`).getTime();
  const { data: revenueInsightsData, loading: revenueInsightsLoading } = useQuery(
    GET_SUPERADMIN_REVENUE_INSIGHTS,
    {
      variables: { startDate: rangeStart, endDate: rangeEnd },
      skip: !isRevenueRangeValid,
    },
  );
  const filteredAttendance = useMemo(() => {
    const list = attendanceData?.getAttendanceReport || [];
    if (!searchTerm) return list;
    return list.filter(r => {
      const search = searchTerm.toLowerCase();
      const pct = Math.round(r.attendancePercentage).toString();
      const attendance = `${r.presentCount}/${r.totalStudents}`;
      const statusStr = r.status.replace("_", " ").toLowerCase();
      const dateStr = formatDate(r.date).toLowerCase();
      
      return (
        r.className.toLowerCase().includes(search) || 
        r.instructorName.toLowerCase().includes(search) ||
        pct.includes(search) ||
        attendance.includes(search) ||
        r.totalStudents.toString().includes(search) ||
        statusStr.includes(search) ||
        dateStr.includes(search)
      );
    });
  }, [attendanceData, searchTerm]);



  const revenueSeries: RevenueInsightPoint[] = useMemo(
    () =>
      !isRevenueRangeValid
        ? []
        : (revenueInsightsData?.getSuperAdminRevenueInsights?.points || []).map((item: RevenueInsightPoint) => ({
            date: item.date,
            month: item.month,
            pass: Number(item.pass || 0),
            passCount: Number(item.passCount || 0),
            classes: Number(item.classes || 0),
            classesCount: Number(item.classesCount || 0),
            event: Number(item.event || 0),
            eventCount: Number(item.eventCount || 0),
          })),
    [isRevenueRangeValid, revenueInsightsData],
  );

  const revenueCounts: RevenueInsightCounts = useMemo(
    () => ({
      pass: isRevenueRangeValid ? revenueInsightsData?.getSuperAdminRevenueInsights?.passCount ?? 0 : 0,
      classes: isRevenueRangeValid ? revenueInsightsData?.getSuperAdminRevenueInsights?.classesCount ?? 0 : 0,
      event: isRevenueRangeValid ? revenueInsightsData?.getSuperAdminRevenueInsights?.eventCount ?? 0 : 0,
    }),
    [isRevenueRangeValid, revenueInsightsData],
  );

  const handleResetRevenueDateRange = () => {
    setRangeStart("");
    setRangeEnd("");
  };

  const handleResetReportFilters = () => {
    setSearchTerm("");
    setTimeRange(defaultTimeRange);
    setMonthYear(defaultMonthYear);
    setSpecificDate(defaultSpecificDate);
    setSpecificYear(defaultSpecificYear);
    setClassFilter("All Classes");
    setInstructorFilter("All Instructors");
    setAttendancePage(1);
    setStudentPage(1);
    setPerformancePage(1);
    setEngagementPage(1);
  };

  const handleExportExcel = (options: ExportOption[]) => {
    const workbook = XLSX.utils.book_new();
    const timestamp = new Date().toISOString().slice(0, 10);
    const attendanceRows = filteredAttendance.map((row) => ({
      "Class Name": row.className,
      Instructor: row.instructorName,
      Date: formatDate(row.date),
      "Total Students": row.totalStudents,
      Attendance: `${row.presentCount} / ${row.totalStudents}`,
      Percentage: `${Math.round(row.attendancePercentage)}%`,
      Status: row.status.replace("_", " "),
    }));
    const performanceRows = (performanceData?.getClassPerformanceReport || []).map((row) => ({
      "Class Name": row.className,
      Capacity: row.totalCapacity,
      Booked: row.bookedCount,
      "Available Slots": row.availableSlots,
      "Fill Rate": `${Math.round(row.fillRatePercentage)}%`,
      Status: row.status.replace("_", " "),
    }));
    const engagementRows = (engagementData?.getInstructorEngagementReport || []).map((row) => ({
      Instructor: row.instructorName,
      "Total Classes": row.totalClasses,
      "Avg Attendance": `${Math.round(row.avgAttendancePercentage)}%`,
    }));
    const studentRows = (studentOverviewData?.getStudentOverviewReport || []).map((row) => ({
      "Student Name": row.studentName,
      "Custom ID": row.customId,
      Membership: row.membershipType,
      "Enrolled Classes": row.enrolledClasses.join(", "),
      Attendance: `${Math.round(row.attendancePercentage)}%`,
      Status: row.isActive ? "Active" : "Inactive",
      "Last Activity": row.lastActivity,
    }));

    const attendanceSheet = XLSX.utils.json_to_sheet(attendanceRows);
    const classPerformanceSheet = XLSX.utils.json_to_sheet(performanceRows);
    const instructorEngagementSheet = XLSX.utils.json_to_sheet(engagementRows);
    const studentOverviewSheet = XLSX.utils.json_to_sheet(studentRows);

    attendanceSheet["!cols"] = [{ wch: 28 }, { wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 16 }];
    classPerformanceSheet["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 16 }];
    instructorEngagementSheet["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 16 }];
    studentOverviewSheet["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 18 }, { wch: 36 }, { wch: 12 }, { wch: 12 }, { wch: 18 }];

    if (options.includes("attendance") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, attendanceSheet, "Attendance");
    }
    if (options.includes("class_performance") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, classPerformanceSheet, "Class Performance");
    }
    if (options.includes("instructor_engagement") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, instructorEngagementSheet, "Instructor Engagement");
    }
    if (options.includes("student_overview") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, studentOverviewSheet, "Student Overview");
    }

    XLSX.writeFile(workbook, `reports-dashboard-${timestamp}.xlsx`);
  };

  const handleToggleExportOption = (option: ExportOption) => {
    if (option === "all") {
      setSelectedExportOptions((current) =>
        current.includes("all") ? [] : ["all", ...INDIVIDUAL_EXPORT_OPTIONS],
      );
      return;
    }

    setSelectedExportOptions((current) => {
      const baseOptions = current.filter((value) => value !== "all");
      const nextOptions = baseOptions.includes(option)
        ? baseOptions.filter((value) => value !== option)
        : [...baseOptions, option];

      if (nextOptions.length === INDIVIDUAL_EXPORT_OPTIONS.length) {
        return ["all", ...INDIVIDUAL_EXPORT_OPTIONS];
      }

      return nextOptions;
    });
  };

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
    setSelectedExportOptions([]);
  };



  return (
    <Stack spacing={2.5} sx={{ pt: 2, px: { xs: 1, sm: 0 }, pb: { xs: 2, sm: 2.5 }, position: "relative" }}>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 500, color: themestyle.colors.primary, letterSpacing: "-0.04em", fontSize: 26 }}
          >
            Reports Dashboard
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} width={{ xs: "100%", md: "auto" }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportMenuClick}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2.5,
              px: 2.25,
              bgcolor: "#a3d65c",
              boxShadow: "none",
              "&:hover": { bgcolor: "#86b347", boxShadow: "none" },
            }}
          >
            Export Excel
          </Button>
          <Menu
            anchorEl={exportMenuAnchorEl}
            open={Boolean(exportMenuAnchorEl)}
            onClose={handleExportMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 260,
                borderRadius: 2.5,
                border: `1px solid ${themestyle.colors.bordercolor}`,
                boxShadow: "0px 16px 40px rgba(7, 24, 51, 0.10)",
                overflow: "hidden",
              },
            }}
          >
            {EXPORT_OPTIONS.map((option) => (
              <MenuItem
                dense
                key={option.value}
                onClick={() => handleToggleExportOption(option.value)}
                sx={{ py: 0.8, fontSize: 13, fontWeight: 600 }}
              >
                <Checkbox
                  checked={selectedExportOptions.includes(option.value)}
                  sx={{
                    p: 0,
                    mr: 1.25,
                    color: "#a3d65c",
                    "&.Mui-checked": { color: "#a3d65c" },
                    "& .MuiSvgIcon-root": { fontSize: 22 },
                  }}
                />
                {option.title}
              </MenuItem>
            ))}
            <Divider />
            <Box sx={{ p: 1.25 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => {
                  handleExportExcel(selectedExportOptions);
                  handleExportMenuClose();
                }}
                disabled={selectedExportOptions.length === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: 2,
                  bgcolor: "#a3d65c",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#86b347", boxShadow: "none" },
                }}
              >
                Download Selected
              </Button>
            </Box>
          </Menu>
        </Stack>
      </Stack>

      <Card elevation={0} sx={panelSx}>
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md:
                  timeRange === "Specific Month" || timeRange === "Specific Date" || timeRange === "Specific Year"
                    ? "minmax(220px, 1.2fr) repeat(4, minmax(180px, 1fr)) auto"
                    : "minmax(220px, 1.2fr) repeat(3, minmax(180px, 1fr)) auto",
              },
              gap: 1.5,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              placeholder="Search reports..."
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
            <Select
              size="small"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              sx={filterFieldSx}
              MenuProps={{ 
                PaperProps: { 
                  sx: { 
                    maxWidth: 200, 
                    maxHeight: 300,
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none"
                  } 
                } 
              }}
            >
              <MenuItem dense value="Today" sx={{ py: 0.5 }}>Today</MenuItem>
              <MenuItem dense value="This Week" sx={{ py: 0.5 }}>This Week</MenuItem>
              <MenuItem dense value="This Month" sx={{ py: 0.5 }}>This Month</MenuItem>
              <MenuItem dense value="This Year" sx={{ py: 0.5 }}>This Year</MenuItem>
              <MenuItem dense value="Specific Month" sx={{ py: 0.5 }}>Specific Month</MenuItem>
              <MenuItem dense value="Specific Date" sx={{ py: 0.5 }}>Specific Date</MenuItem>
              <MenuItem dense value="Specific Year" sx={{ py: 0.5 }}>Specific Year</MenuItem>
            </Select>
            {timeRange === "Specific Month" && (
              <TextField
                type="month"
                size="small"
                value={monthYear}
                onChange={(e) => setMonthYear(e.target.value)}
                sx={{
                  ...filterFieldSx,
                  "& input": { 
                    py: 1, 
                    fontWeight: 700, 
                    color: themestyle.colors.primary,
                    cursor: "pointer"
                  }
                }}
              />
            )}
            {timeRange === "Specific Date" && (
              <AppDatePicker
                value={specificDate}
                onChange={(v) => setSpecificDate(v)}
              />
            )}
            {timeRange === "Specific Year" && (
              <TextField
                type="number"
                size="small"
                value={specificYear}
                onChange={(e) => setSpecificYear(e.target.value)}
                sx={{
                  ...filterFieldSx,
                  "& input": { 
                    py: 1, 
                    fontWeight: 700, 
                    color: themestyle.colors.primary,
                  }
                }}
              />
            )}
            <Select
              size="small"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              sx={filterFieldSx}
              MenuProps={{ 
                PaperProps: { 
                  sx: { 
                    maxWidth: 250, 
                    maxHeight: 300,
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none"
                  } 
                } 
              }}
            >
              <MenuItem dense value="All Classes" sx={{ py: 0.5 }}>All Classes</MenuItem>
              {classListData?.getClassList.data.map((c: any) => (
                <MenuItem dense key={c.id} value={c.classId} sx={{ py: 0.5 }}>{c.className}</MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={instructorFilter}
              onChange={(e) => setInstructorFilter(e.target.value)}
              sx={filterFieldSx}
              MenuProps={{ 
                PaperProps: { 
                  sx: { 
                    maxWidth: 200, 
                    maxHeight: 300,
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none"
                  } 
                } 
              }}
            >
              <MenuItem dense value="All Instructors" sx={{ py: 0.5 }}>All Instructors</MenuItem>
              {instructorListData?.getInstructorList.data.map((i: any) => (
                <MenuItem dense key={i.id} value={i.id} sx={{ py: 0.5 }}>{i.fullname}</MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetReportFilters}
              disabled={!canResetReportFilters}
              sx={{
                minWidth: { xs: "100%", sm: 92 },
                height: 40,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                color: canResetReportFilters ? themestyle.colors.primary : "#9CA3AF",
                borderColor: canResetReportFilters ? themestyle.colors.bordercolor : "#D1D5DB",
              }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={panelSx}>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ px: 2.5, py: 2 }}
          >
            <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
              Attendance Report
            </Typography>
            {filteredAttendance.length > ATTENDANCE_PER_PAGE && (
              <ResponsivePagination
                count={Math.ceil(filteredAttendance.length / ATTENDANCE_PER_PAGE)}
                page={attendancePage}
                onChange={(val) => setAttendancePage(val)}
              />
            )}
          </Stack>
          <Divider />
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  {["Class Name", "Instructor", "Date", "Total Students", "Attendance", "Percentage", "Status"].map((label) => (
                    <TableCell 
                      key={label} 
                      sx={{
                        ...tableHeadCellSx,
                        ...(label === "Class Name" ? { width: 220, minWidth: 220 } : {})
                      }}
                      align={["Total Students", "Attendance", "Percentage", "Status"].includes(label) ? "center" : "left"}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceLoading &&
                (!attendanceData || attendanceData.getAttendanceReport.length === 0) ? (
                  <ReportTableState
                    colSpan={7}
                    loading
                    message="Loading attendance data..."
                  />
                ) : (
                  <>
                    {filteredAttendance
                      .slice((attendancePage - 1) * ATTENDANCE_PER_PAGE, attendancePage * ATTENDANCE_PER_PAGE)
                      .map((row: AttendanceReport, index: number) => (
                        <TableRow 
                          key={`${row.classId}-${row.date}-${index}`} 
                          sx={{ 
                            ...tableRowSx, 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#F9FAFB" }
                          }}
                          onClick={() => navigate(`/instructors/report/${row.instructorId}`)}
                        >
                          <TableCell sx={{ ...tableBodyStrongCellSx, width: 220, maxWidth: 220 }}>
                            <TruncatedTitle 
                              text={row.className} 
                              variant="body2" 
                              maxLines={1}
                              sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}
                            />
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>{row.instructorName}</TableCell>
                          <TableCell sx={tableBodyCellSx}>{formatDate(row.date)}</TableCell>
                          <TableCell sx={tableBodyCellSx} align="center">{row.totalStudents}</TableCell>
                          <TableCell sx={tableBodyStrongCellSx} align="center">
                            {row.presentCount} / {row.totalStudents}
                          </TableCell>
                          <TableCell sx={{ ...tableBodyStrongCellSx, color: themestyle.colors.success }} align="center">
                            {Math.round(row.attendancePercentage)}%
                          </TableCell>
                          <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                            <Chip
                              label={row.status.replace("_", " ")}
                              sx={{
                                bgcolor: statusChipStyles[row.status]?.bg || "#F3F4F6",
                                color: statusChipStyles[row.status]?.fg || "#374151",
                                border: `1.5px solid ${statusChipStyles[row.status]?.border || "#E5E7EB"}`,
                                fontWeight: 900,
                                fontSize: "10px",
                                borderRadius: "999px",
                                minWidth: 120,
                                height: 30,
                                "& .MuiChip-label": {
                                  px: 1.5,
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredAttendance.length === 0 && (
                      <ReportTableState
                        colSpan={7}
                        message="No attendance data found for this period."
                      />
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={panelSx}>
        <CardContent sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: themestyle.colors.primary }}>
              Revenue Insights
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={{ mb: 0.75, color: "#111827", fontWeight: 600, fontSize: 14 }}>
                  From Date
                </Typography>
                <AppDatePicker
                  value={rangeStart}
                  onChange={(v) => setRangeStart(v)}
                  error={
                    (hasRangeStart && !isRangeStartValid) ||
                    (hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid)
                  }
                  helperText={
                    hasRangeStart && !isRangeStartValid
                      ? "Enter a valid date"
                      : hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid
                        ? "From Date must be on or before To Date"
                        : " "
                  }
                />
              </Box>
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={{ mb: 0.75, color: "#111827", fontWeight: 600, fontSize: 14 }}>
                  To Date
                </Typography>
                <AppDatePicker
                  value={rangeEnd}
                  onChange={(v) => setRangeEnd(v)}
                  error={
                    (hasRangeEnd && !isRangeEndValid) ||
                    (hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid)
                  }
                  helperText={
                    hasRangeEnd && !isRangeEndValid
                      ? "Enter a valid date"
                      : hasRangeStart && hasRangeEnd && isRangeStartValid && isRangeEndValid && !isRevenueRangeValid
                        ? "To Date must be on or after From Date"
                        : " "
                  }
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "stretch", sm: "flex-start" },
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    mb: 0.75,
                    fontWeight: 600,
                    fontSize: 14,
                    visibility: "hidden",
                  }}
                >
                  Reset
                </Typography>
                <Box sx={{ height: 40, display: "flex", alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResetRevenueDateRange}
                    disabled={!canResetRevenueDateRange}
                    sx={{
                      minWidth: { xs: "100%", sm: 92 },
                      height: 40,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      color: canResetRevenueDateRange ? themestyle.colors.primary : "#9CA3AF",
                      borderColor: canResetRevenueDateRange ? themestyle.colors.bordercolor : "#D1D5DB",
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Stack>
          <RevenueInsightsChart
            series={revenueSeries}
            counts={revenueCounts}
            showCounts
            fullWidth={true}
            loading={revenueInsightsLoading}
            emptyMessage={
              hasRangeStart || hasRangeEnd
                ? "No revenue data found for the selected date range."
                : "Select a date range to view revenue insights."
            }
          />
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.3fr 0.9fr" },
          gap: 2,
        }}
      >
        <Card elevation={0} sx={panelSx}>
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2.5, py: 2 }}
            >
              <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
                Class Performance
              </Typography>
              {(performanceData?.getClassPerformanceReport?.length || 0) > PERFORMANCE_PER_PAGE && (
                <ResponsivePagination
                  count={Math.ceil((performanceData?.getClassPerformanceReport?.length || 0) / PERFORMANCE_PER_PAGE)}
                  page={performancePage}
                  onChange={(val) => setPerformancePage(val)}
                />
              )}
            </Stack>
            <Divider />
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    {["Class Name", "Instructor", "Bookings", "Fill Rate", "Status"].map((label) => (
                      <TableCell 
                        key={label} 
                        sx={{
                          ...tableHeadCellSx,
                          ...(label === "Class Name" ? { width: 200, minWidth: 200 } : {})
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performanceLoading &&
                  (!performanceData || (performanceData.getClassPerformanceReport?.length || 0) === 0) ? (
                    <ReportTableState
                      colSpan={5}
                      loading
                      message="Loading class performance data..."
                    />
                  ) : (
                    <>
                      {performanceData?.getClassPerformanceReport
                        .slice((performancePage - 1) * PERFORMANCE_PER_PAGE, performancePage * PERFORMANCE_PER_PAGE)
                        .map((row) => (
                          <TableRow key={row.classId} sx={tableRowSx}>
                            <TableCell sx={{ ...tableBodyStrongCellSx, width: 200, maxWidth: 200 }}>
                              <TruncatedTitle 
                                text={row.className} 
                                variant="body2" 
                                maxLines={1}
                                sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}
                              />
                            </TableCell>
                            <TableCell sx={tableBodyCellSx}>{row.instructorName}</TableCell>
                            <TableCell sx={tableBodyCellSx}>{row.bookedCount} / {row.totalCapacity}</TableCell>
                            <TableCell sx={tableBodyCellSx}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <LinearProgress
                                  variant="determinate"
                                  value={row.fillRatePercentage}
                                  sx={{
                                    width: 80,
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: "#E5E7EB",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 999,
                                      bgcolor:
                                        row.fillRatePercentage > 80
                                          ? themestyle.colors.success
                                          : row.fillRatePercentage > 40
                                            ? "#3B82F6"
                                            : "#F59E0B",
                                    },
                                  }}
                                />
                                <Typography sx={{ fontWeight: 800, color: themestyle.colors.primary, fontSize: 13 }}>
                                  {Math.round(row.fillRatePercentage)}%
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={tableBodyCellSx}>
                              <Chip
                                label={row.status.replace("_", " ")}
                                size="small"
                                sx={{
                                  bgcolor: performanceChipStyles[row.status]?.bg || "#F3F4F6",
                                  color: performanceChipStyles[row.status]?.fg || "#6B7280",
                                  fontWeight: 900,
                                  fontSize: "10px",
                                  borderRadius: 999,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      {(!performanceData?.getClassPerformanceReport || performanceData.getClassPerformanceReport.length === 0) && (
                        <ReportTableState
                          colSpan={5}
                          message="No class performance data found."
                        />
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={0} sx={panelSx}>
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2.5, py: 2 }}
            >
              <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
                Instructor Engagement
              </Typography>
              {(engagementData?.getInstructorEngagementReport?.length || 0) > ENGAGEMENT_PER_PAGE && (
                <ResponsivePagination
                  count={Math.ceil((engagementData?.getInstructorEngagementReport?.length || 0) / ENGAGEMENT_PER_PAGE)}
                  page={engagementPage}
                  onChange={(val) => setEngagementPage(val)}
                />
              )}
            </Stack>
            <Divider />
            <Box sx={{ overflowX: "hidden" }}>
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    {["Instructor", "Classes", "Canceled", "Average Attendance"] .map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          ...tableHeadCellSx,
                          width:
                            label === "Instructor"
                              ? "36%"
                              : label === "Average Attendance"
                                ? "28%"
                                : "18%",
                          px: 1.5,
                          fontSize: 12,
                        }}
                        align={label !== "Instructor" ? "center" : "left"}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {engagementLoading && (!engagementData || (engagementData.getInstructorEngagementReport?.length || 0) === 0) ? (
                    <ReportTableState
                      colSpan={4}
                      loading
                      message="Loading engagement data..."
                    />
                  ) : (
                    <>
                      {engagementData?.getInstructorEngagementReport
                        .slice((engagementPage - 1) * ENGAGEMENT_PER_PAGE, engagementPage * ENGAGEMENT_PER_PAGE)
                        .map((row) => (
                        <TableRow 
                          key={row.instructorId} 
                          sx={{ 
                            ...tableRowSx, 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#F9FAFB" }
                          }}
                          onClick={() => navigate(`/instructors/report/${row.instructorId}`)}
                        >
                          <TableCell sx={{ ...tableBodyCellSx, width: "40%", px: 1.5, py: 1.5, minWidth: 0 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  bgcolor: "#a3d65c",
                                  color: "#fff",
                                  fontWeight: 900,
                                  fontSize: 12,
                                  flexShrink: 0,
                                  boxShadow: "0 4px 12px rgba(163,214,92,0.35)",
                                }}
                              >
                                {initials(row.instructorName)}
                              </Avatar>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: themestyle.colors.primary,
                                  fontSize: 13,
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={row.instructorName}
                              >
                                {truncateText(row.instructorName, 10)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ ...tableBodyCellSx, px: 1, py: 1.5, fontSize: 13 }} align="center">{row.totalClasses}</TableCell>
                          <TableCell sx={{ ...tableBodyStrongCellSx, color: "#EF4444", px: 1, py: 1.5, fontSize: 13 }} align="center">{row.cancelledClassesCount}</TableCell>
                          <TableCell sx={{ ...tableBodyStrongCellSx, color: themestyle.colors.success, px: 1, py: 1.5, fontSize: 13 }} align="center">
                            {Math.round(row.avgAttendancePercentage)}%
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!engagementData?.getInstructorEngagementReport || engagementData.getInstructorEngagementReport.length === 0) && (
                        <ReportTableState
                          colSpan={4}
                          message="No instructor engagement data found."
                        />
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card elevation={0} sx={panelSx}>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ px: 2.5, py: 2 }}
          >
            <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
              Student Overview
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {(studentOverviewData?.getStudentOverviewReport?.length || 0) > STUDENT_PER_PAGE && (
                <ResponsivePagination
                  count={Math.ceil((studentOverviewData?.getStudentOverviewReport?.length || 0) / STUDENT_PER_PAGE)}
                  page={studentPage}
                  onChange={(val) => setStudentPage(val)}
                />
              )}
              <Button
                variant="text"
                onClick={() => navigate("/students")}
                sx={{
                  p: 0,
                  minWidth: 0,
                  color: themestyle.colors.success,
                  "&:hover": { bgcolor: "transparent", opacity: 0.8 },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 20 }} />
              </Button>
            </Stack>
          </Stack>
          <Divider />
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  {["Student Name", "Enrolled Classes", "Attendance %", "Last Activity"].map((label) => (
                    <TableCell key={label} sx={tableHeadCellSx}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {overviewLoading &&
                (!studentOverviewData || (studentOverviewData.getStudentOverviewReport?.length || 0) === 0) ? (
                  <ReportTableState
                    colSpan={4}
                    loading
                    message="Loading student overview..."
                  />
                ) : (
                  <>
                    {studentOverviewData?.getStudentOverviewReport
                      .slice((studentPage - 1) * STUDENT_PER_PAGE, studentPage * STUDENT_PER_PAGE)
                      .map((row) => (
                        <TableRow 
                          key={row.studentId} 
                          sx={{ 
                            ...tableRowSx, 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#F9FAFB" }
                          }}
                          onClick={() => navigate(`/students/report/${row.studentId}`)}
                        >
                          <TableCell sx={tableBodyCellSx}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: "#a3d65c",
                                  color: "#fff",
                                  fontWeight: 900,
                                  fontSize: 14,
                                  boxShadow: "0 4px 12px rgba(163,214,92,0.35)",
                                }}
                              >
                                {initials(row.studentName)}
                              </Avatar>
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography sx={{ fontWeight: 700, color: themestyle.colors.primary, lineHeight: 1.2 }}>
                                    {row.studentName}
                                  </Typography>
                                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF" }}>
                                    {row.customId}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                                  <Box 
                                    sx={{ 
                                      width: 6, 
                                      height: 6, 
                                      borderRadius: "50%", 
                                      bgcolor: row.isActive ? "#10B981" : "#9CA3AF" 
                                    }} 
                                  />
                                  <Typography sx={{ fontSize: "10px", fontWeight: 600, color: row.isActive ? "#10B981" : "#6B7280", textTransform: "uppercase", letterSpacing: "0.02em" }}>
                                    {row.isActive ? "Active" : "Inactive"}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 300 }}>
                              {row.enrolledClasses?.length > 0 ? (
                                row.enrolledClasses.map((cls, idx) => (
                                  <Chip 
                                    key={idx} 
                                    label={cls} 
                                    size="small" 
                                    sx={{ 
                                      height: 20, 
                                      fontSize: "10px", 
                                      fontWeight: 700,
                                      bgcolor: "#F3F4F6",
                                      color: "#374151"
                                    }} 
                                  />
                                ))
                              ) : (
                                <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>No Classes</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tableBodyStrongCellSx, color: themestyle.colors.success }}>
                            {Math.round(row.attendancePercentage)}%
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>{formatDate(row.lastActivity)}</TableCell>
                        </TableRow>
                      ))}
                    {(!studentOverviewData?.getStudentOverviewReport || studentOverviewData.getStudentOverviewReport.length === 0) && (
                      <ReportTableState
                        colSpan={4}
                        message="No student overview data found."
                      />
                    )}
                  </>
                )}

                </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}

const filterFieldSx = {
  bgcolor: "#F9FAFB",
  borderRadius: "2rem",
  "& .MuiOutlinedInput-root": {
    borderRadius: "2rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E5E7EB",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#a3d65c !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#a3d65c !important",
    },
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#a3d65c !important",
  },
};

const tableHeadCellSx = {
  color: "#000",
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: "0.02em",
  borderBottom: "1px solid #F3F4F6",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  color: "#6B7280",
  fontWeight: 600,
  borderBottom: "1px solid #F3F4F6",
  py: 2,
};

const tableBodyStrongCellSx = {
  ...tableBodyCellSx,
  color: "#6B7280",
  fontWeight: 700,
};

const tableRowSx = {
  "&:last-child td": {
    borderBottom: "none",
  },
};
